import {
  VARIAVEIS_MENSAGEM_RAPIDA,
  categoriaMotivoFormSchema,
  mensagemRapidaFormSchema,
  rotuloSetores,
  tagFormSchema,
  type CategoriaComMotivos,
  type CategoriaMotivoForm,
  type MensagemRapidaForm,
  type MensagemRapidaResumo,
  type TagForm,
  type TagResumo,
} from '@mymaxx/shared';
import { Aviso, Botao, CampoTexto, Modal } from '@mymaxx/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FolderPlus, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { SeletorSetores } from '../componentes/SeletorSetores';
import { ErroApi } from '../lib/api';
import { arrumarAtalho } from '../lib/atalho';
import { gestaoApi } from '../lib/gestao-api';

type Aba = 'motivos' | 'respostas';

const CATEGORIA_VAZIA: CategoriaMotivoForm = { nome: '', ordem: 0, ativo: true };
const TAG_VAZIA: TagForm = {
  categoriaId: '',
  nome: '',
  ordem: 0,
  setorIds: [],
  dispensaAvaliacao: false,
  ativo: true,
};
const MENSAGEM_VAZIA: MensagemRapidaForm = {
  atalho: '',
  titulo: '',
  conteudo: '',
  setorIds: [],
  ativo: true,
};

/** Catálogos do atendimento: motivos em dois níveis (categoria → motivo) e respostas prontas. */
export function Atendimento() {
  const [aba, setAba] = useState<Aba>('motivos');

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold">Motivos e respostas</h1>
        <p className="text-sm text-neutral-500">
          Usados pelo atendente ao encerrar uma conversa e para responder mais rápido.
        </p>
      </header>

      <div className="flex gap-1 border-b border-neutral-200 dark:border-neutral-800">
        {(
          [
            ['motivos', 'Motivos de encerramento'],
            ['respostas', 'Respostas prontas'],
          ] as const
        ).map(([valor, rotulo]) => (
          <button
            key={valor}
            type="button"
            onClick={() => setAba(valor)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm transition ${
              aba === valor
                ? 'border-marca-600 font-medium text-marca-700 dark:text-marca-300'
                : 'border-transparent text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200'
            }`}
          >
            {rotulo}
          </button>
        ))}
      </div>

      {aba === 'motivos' ? <Motivos /> : <Respostas />}
    </div>
  );
}

function Motivos() {
  const queryClient = useQueryClient();
  const [editandoCategoria, setEditandoCategoria] = useState<{
    id: string | null;
    form: CategoriaMotivoForm;
  } | null>(null);
  const [editandoMotivo, setEditandoMotivo] = useState<{ id: string | null; form: TagForm } | null>(
    null,
  );
  const [erro, setErro] = useState<string | null>(null);

  const arvore = useQuery({ queryKey: ['motivos'], queryFn: gestaoApi.listarMotivos });
  const setores = useQuery({ queryKey: ['setores'], queryFn: gestaoApi.listarSetores });
  const recarregar = () => {
    void queryClient.invalidateQueries({ queryKey: ['motivos'] });
    void queryClient.invalidateQueries({ queryKey: ['categorias-motivo'] });
  };
  const tratarErro = (e: unknown) => setErro(e instanceof ErroApi ? e.mensagem : 'Falha ao salvar');
  const aoSalvar = () => {
    recarregar();
    setEditandoCategoria(null);
    setEditandoMotivo(null);
    setErro(null);
  };

  const salvarCategoria = useMutation({
    mutationFn: ({ id, form }: { id: string | null; form: CategoriaMotivoForm }) =>
      id ? gestaoApi.atualizarCategoria(id, form) : gestaoApi.criarCategoria(form),
    onSuccess: aoSalvar,
    onError: tratarErro,
  });
  const removerCategoria = useMutation({
    mutationFn: (id: string) => gestaoApi.removerCategoria(id),
    onSuccess: recarregar,
    onError: tratarErro,
  });
  const salvarMotivo = useMutation({
    mutationFn: ({ id, form }: { id: string | null; form: TagForm }) =>
      id ? gestaoApi.atualizarTag(id, form) : gestaoApi.criarTag(form),
    onSuccess: aoSalvar,
    onError: tratarErro,
  });
  const removerMotivo = useMutation({
    mutationFn: (id: string) => gestaoApi.removerTag(id),
    onSuccess: recarregar,
    onError: tratarErro,
  });

  const categorias = arvore.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-neutral-500">
          Dois níveis: a <strong>categoria</strong> agrupa os <strong>motivos</strong>. Todo motivo
          pertence a uma categoria.
        </p>
        <div className="flex gap-2">
          <Botao
            variante="secundario"
            onClick={() => {
              setEditandoCategoria({ id: null, form: { ...CATEGORIA_VAZIA } });
              setErro(null);
            }}
          >
            <FolderPlus size={16} /> Nova categoria
          </Botao>
          <Botao
            disabled={categorias.length === 0}
            onClick={() => {
              setEditandoMotivo({
                id: null,
                form: { ...TAG_VAZIA, categoriaId: categorias[0]?.id ?? '' },
              });
              setErro(null);
            }}
          >
            <Plus size={16} /> Novo motivo
          </Botao>
        </div>
      </div>

      {erro && !editandoCategoria && !editandoMotivo && <Aviso tipo="erro">{erro}</Aviso>}

      {categorias.length === 0 && !arvore.isPending && (
        <div className="rounded-card border border-dashed border-neutral-300 px-4 py-8 text-center text-sm text-neutral-500 dark:border-neutral-700">
          Comece criando uma categoria (ex.: &quot;Problemas de conexão&quot;) e depois os motivos
          dentro dela.
        </div>
      )}

      <div className="space-y-3">
        {categorias.map((categoria: CategoriaComMotivos) => (
          <section
            key={categoria.id}
            className="rounded-card border border-neutral-200 dark:border-neutral-800"
          >
            <header className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-200 px-4 py-3 dark:border-neutral-800">
              <div>
                <p className={`font-medium ${categoria.ativo ? '' : 'text-neutral-400'}`}>
                  {categoria.nome}
                  {!categoria.ativo && <span className="ml-2 text-xs">(inativa)</span>}
                </p>
                <p className="text-xs text-neutral-500">
                  Categoria · {categoria.totalMotivos} motivo(s)
                </p>
              </div>
              <div className="flex gap-1">
                <Botao
                  variante="secundario"
                  onClick={() =>
                    setEditandoCategoria({
                      id: categoria.id,
                      form: {
                        nome: categoria.nome,
                        ordem: categoria.ordem,
                        ativo: categoria.ativo,
                      },
                    })
                  }
                >
                  <Pencil size={14} />
                </Botao>
                <Botao
                  variante="secundario"
                  onClick={() => {
                    if (confirm(`Excluir a categoria ${categoria.nome}?`)) {
                      removerCategoria.mutate(categoria.id);
                    }
                  }}
                >
                  <Trash2 size={14} />
                </Botao>
                <Botao
                  variante="secundario"
                  onClick={() => {
                    setEditandoMotivo({
                      id: null,
                      form: { ...TAG_VAZIA, categoriaId: categoria.id },
                    });
                    setErro(null);
                  }}
                >
                  <Plus size={14} /> Motivo
                </Botao>
              </div>
            </header>

            <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
              {categoria.motivos.map((motivo: TagResumo) => (
                <li key={motivo.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                  <div className="min-w-0">
                    <p className={`text-sm ${motivo.ativo ? '' : 'text-neutral-400 line-through'}`}>
                      {motivo.nome}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {rotuloSetores(motivo.setores)} · usado {motivo.totalUsos}×
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <Botao
                      variante="secundario"
                      onClick={() =>
                        setEditandoMotivo({
                          id: motivo.id,
                          form: {
                            categoriaId: motivo.categoria.id,
                            nome: motivo.nome,
                            ordem: motivo.ordem,
                            setorIds: motivo.setores.map((s) => s.id),
                            dispensaAvaliacao: motivo.dispensaAvaliacao,
                            ativo: motivo.ativo,
                          },
                        })
                      }
                    >
                      <Pencil size={14} />
                    </Botao>
                    <Botao
                      variante="secundario"
                      onClick={() => {
                        const aviso =
                          motivo.totalUsos > 0
                            ? 'Este motivo já foi usado, então será apenas desativado. Continuar?'
                            : `Excluir o motivo ${motivo.nome}?`;
                        if (confirm(aviso)) removerMotivo.mutate(motivo.id);
                      }}
                    >
                      <Trash2 size={14} />
                    </Botao>
                  </div>
                </li>
              ))}
              {categoria.motivos.length === 0 && (
                <li className="px-4 py-4 text-center text-xs text-neutral-500">
                  Nenhum motivo nesta categoria.
                </li>
              )}
            </ul>
          </section>
        ))}
      </div>

      {/* Categoria */}
      <Modal
        aberto={!!editandoCategoria}
        titulo={editandoCategoria?.id ? 'Editar categoria' : 'Nova categoria'}
        descricao="Nível 1 — agrupa os motivos que o atendente escolhe ao encerrar."
        onFechar={() => setEditandoCategoria(null)}
        acoes={
          <>
            <Botao variante="secundario" onClick={() => setEditandoCategoria(null)}>
              Cancelar
            </Botao>
            <Botao
              carregando={salvarCategoria.isPending}
              onClick={() => {
                if (!editandoCategoria) return;
                const parse = categoriaMotivoFormSchema.safeParse(editandoCategoria.form);
                if (!parse.success) {
                  setErro(parse.error.issues[0]?.message ?? 'Confira os campos');
                  return;
                }
                salvarCategoria.mutate({ id: editandoCategoria.id, form: parse.data });
              }}
            >
              Salvar
            </Botao>
          </>
        }
      >
        {editandoCategoria && (
          <div className="space-y-4">
            <CampoTexto
              rotulo="Nome"
              autoFocus
              value={editandoCategoria.form.nome}
              onChange={(e) =>
                setEditandoCategoria({
                  ...editandoCategoria,
                  form: { ...editandoCategoria.form, nome: e.target.value },
                })
              }
            />
            <CampoTexto
              rotulo="Ordem"
              type="number"
              min={0}
              value={editandoCategoria.form.ordem}
              onChange={(e) =>
                setEditandoCategoria({
                  ...editandoCategoria,
                  form: { ...editandoCategoria.form, ordem: Number(e.target.value) },
                })
              }
              ajuda="Menor número aparece primeiro."
            />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={editandoCategoria.form.ativo}
                onChange={(e) =>
                  setEditandoCategoria({
                    ...editandoCategoria,
                    form: { ...editandoCategoria.form, ativo: e.target.checked },
                  })
                }
              />
              Categoria ativa
            </label>
            {erro && <Aviso tipo="erro">{erro}</Aviso>}
          </div>
        )}
      </Modal>

      {/* Motivo */}
      <Modal
        aberto={!!editandoMotivo}
        titulo={editandoMotivo?.id ? 'Editar motivo' : 'Novo motivo'}
        descricao="Nível 2 — o motivo em si, dentro de uma categoria."
        onFechar={() => setEditandoMotivo(null)}
        acoes={
          <>
            <Botao variante="secundario" onClick={() => setEditandoMotivo(null)}>
              Cancelar
            </Botao>
            <Botao
              carregando={salvarMotivo.isPending}
              onClick={() => {
                if (!editandoMotivo) return;
                const parse = tagFormSchema.safeParse(editandoMotivo.form);
                if (!parse.success) {
                  setErro(parse.error.issues[0]?.message ?? 'Confira os campos');
                  return;
                }
                salvarMotivo.mutate({ id: editandoMotivo.id, form: parse.data });
              }}
            >
              Salvar
            </Botao>
          </>
        }
      >
        {editandoMotivo && (
          <div className="space-y-4">
            <label className="block text-sm font-medium">
              Categoria
              <select
                value={editandoMotivo.form.categoriaId}
                onChange={(e) =>
                  setEditandoMotivo({
                    ...editandoMotivo,
                    form: { ...editandoMotivo.form, categoriaId: e.target.value },
                  })
                }
                className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-900"
              >
                <option value="">Selecione…</option>
                {categorias.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>
            </label>

            <div className="grid gap-4 sm:grid-cols-2">
              <CampoTexto
                rotulo="Nome do motivo"
                autoFocus
                value={editandoMotivo.form.nome}
                onChange={(e) =>
                  setEditandoMotivo({
                    ...editandoMotivo,
                    form: { ...editandoMotivo.form, nome: e.target.value },
                  })
                }
              />
              <CampoTexto
                rotulo="Ordem"
                type="number"
                min={0}
                value={editandoMotivo.form.ordem}
                onChange={(e) =>
                  setEditandoMotivo({
                    ...editandoMotivo,
                    form: { ...editandoMotivo.form, ordem: Number(e.target.value) },
                  })
                }
              />
            </div>

            <SeletorSetores
              valor={editandoMotivo.form.setorIds}
              opcoes={setores.data ?? []}
              onChange={(setorIds) =>
                setEditandoMotivo({ ...editandoMotivo, form: { ...editandoMotivo.form, setorIds } })
              }
              rotulo="Válido nos setores"
            />

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={editandoMotivo.form.dispensaAvaliacao}
                onChange={(e) =>
                  setEditandoMotivo({
                    ...editandoMotivo,
                    form: { ...editandoMotivo.form, dispensaAvaliacao: e.target.checked },
                  })
                }
              />
              <span>
                Não pedir avaliação ao cliente
                <span className="block text-xs text-neutral-500">
                  Para motivos em que o cliente sumiu, como &quot;Inatividade&quot;. Pedir nota a
                  quem não está mais ali não mede nada, e a nota pendente ainda trancaria o próximo
                  atendimento dele.
                </span>
              </span>
            </label>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={editandoMotivo.form.ativo}
                onChange={(e) =>
                  setEditandoMotivo({
                    ...editandoMotivo,
                    form: { ...editandoMotivo.form, ativo: e.target.checked },
                  })
                }
              />
              Disponível para os atendentes
            </label>

            {erro && <Aviso tipo="erro">{erro}</Aviso>}
          </div>
        )}
      </Modal>
    </div>
  );
}

function Respostas() {
  const queryClient = useQueryClient();
  const [editando, setEditando] = useState<{ id: string | null; form: MensagemRapidaForm } | null>(
    null,
  );
  const [erro, setErro] = useState<string | null>(null);

  const mensagens = useQuery({
    queryKey: ['mensagens-rapidas'],
    queryFn: gestaoApi.listarMensagens,
  });
  const setores = useQuery({ queryKey: ['setores'], queryFn: gestaoApi.listarSetores });
  const recarregar = () => void queryClient.invalidateQueries({ queryKey: ['mensagens-rapidas'] });
  const tratarErro = (e: unknown) => setErro(e instanceof ErroApi ? e.mensagem : 'Falha ao salvar');

  const salvar = useMutation({
    mutationFn: ({ id, form }: { id: string | null; form: MensagemRapidaForm }) =>
      id ? gestaoApi.atualizarMensagem(id, form) : gestaoApi.criarMensagem(form),
    onSuccess: () => {
      recarregar();
      setEditando(null);
      setErro(null);
    },
    onError: tratarErro,
  });
  const remover = useMutation({
    mutationFn: (id: string) => gestaoApi.removerMensagem(id),
    onSuccess: recarregar,
    onError: tratarErro,
  });

  function enviar() {
    if (!editando) return;
    const parse = mensagemRapidaFormSchema.safeParse(editando.form);
    if (!parse.success) return setErro(parse.error.issues[0]?.message ?? 'Confira os campos');
    salvar.mutate({ id: editando.id, form: parse.data });
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Botao
          onClick={() => {
            setEditando({ id: null, form: { ...MENSAGEM_VAZIA } });
            setErro(null);
          }}
        >
          <Plus size={16} /> Nova resposta
        </Botao>
      </div>
      {erro && !editando && <Aviso tipo="erro">{erro}</Aviso>}

      <ul className="grid gap-3 md:grid-cols-2">
        {mensagens.data?.map((m: MensagemRapidaResumo) => (
          <li
            key={m.id}
            className="rounded-card border border-neutral-200 p-4 dark:border-neutral-800"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium">{m.titulo}</p>
                <code className="text-xs text-marca-600">/{m.atalho}</code>
              </div>
              <div className="flex shrink-0 gap-1">
                <Botao
                  variante="secundario"
                  onClick={() =>
                    setEditando({
                      id: m.id,
                      form: {
                        atalho: m.atalho,
                        titulo: m.titulo,
                        conteudo: m.conteudo,
                        setorIds: m.setores.map((s) => s.id),
                        ativo: m.ativo,
                      },
                    })
                  }
                >
                  <Pencil size={14} />
                </Botao>
                <Botao
                  variante="secundario"
                  onClick={() => {
                    if (confirm(`Excluir a resposta "${m.titulo}"?`)) remover.mutate(m.id);
                  }}
                >
                  <Trash2 size={14} />
                </Botao>
              </div>
            </div>
            <p className="mt-2 line-clamp-3 text-sm text-neutral-600 dark:text-neutral-400">
              {m.conteudo}
            </p>
            <p className="mt-2 text-xs text-neutral-500">{rotuloSetores(m.setores)}</p>
          </li>
        ))}
        {mensagens.data?.length === 0 && (
          <li className="rounded-card border border-dashed border-neutral-300 px-4 py-6 text-center text-sm text-neutral-500 md:col-span-2 dark:border-neutral-700">
            Nenhuma resposta pronta. O atendente as usa digitando /atalho no chat.
          </li>
        )}
      </ul>

      <Modal
        aberto={!!editando}
        titulo={editando?.id ? 'Editar resposta' : 'Nova resposta'}
        largura="larga"
        onFechar={() => setEditando(null)}
        acoes={
          <>
            <Botao variante="secundario" onClick={() => setEditando(null)}>
              Cancelar
            </Botao>
            <Botao onClick={enviar} carregando={salvar.isPending}>
              Salvar
            </Botao>
          </>
        }
      >
        {editando && (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <CampoTexto
                rotulo="Título"
                autoFocus
                value={editando.form.titulo}
                onChange={(e) =>
                  setEditando({ ...editando, form: { ...editando.form, titulo: e.target.value } })
                }
              />
              <CampoTexto
                rotulo="Atalho"
                value={editando.form.atalho}
                onChange={(e) =>
                  setEditando({
                    ...editando,
                    form: { ...editando.form, atalho: arrumarAtalho(e.target.value) },
                  })
                }
                ajuda="O atendente digita /atalho no chat. Escreva sem a barra."
              />
            </div>

            <label className="block text-sm font-medium">
              Mensagem
              <textarea
                rows={4}
                value={editando.form.conteudo}
                onChange={(e) =>
                  setEditando({ ...editando, form: { ...editando.form, conteudo: e.target.value } })
                }
                className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-900"
              />
            </label>

            <div className="rounded-lg bg-neutral-50 p-3 text-xs dark:bg-neutral-900">
              <p className="mb-1 font-medium">Variáveis disponíveis</p>
              <ul className="space-y-0.5 text-neutral-600 dark:text-neutral-400">
                {Object.entries(VARIAVEIS_MENSAGEM_RAPIDA).map(([variavel, descricao]) => (
                  <li key={variavel}>
                    <code className="text-marca-600">{variavel}</code> — {descricao}
                  </li>
                ))}
              </ul>
            </div>

            <SeletorSetores
              valor={editando.form.setorIds}
              opcoes={setores.data ?? []}
              onChange={(setorIds) =>
                setEditando({ ...editando, form: { ...editando.form, setorIds } })
              }
              rotulo="Disponível nos setores"
            />

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={editando.form.ativo}
                onChange={(e) =>
                  setEditando({ ...editando, form: { ...editando.form, ativo: e.target.checked } })
                }
              />
              Disponível para os atendentes
            </label>

            {erro && <Aviso tipo="erro">{erro}</Aviso>}
          </div>
        )}
      </Modal>
    </div>
  );
}
